import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { buildKeys, calibrationKeys, toeCheckKeys, yawCheckKeys } from "../query-keys";
import { uploadWithProgress } from "@/lib/upload-with-progress";
import type { BuildId, CalibrationId, PhotoCategoryId, PhotoId, ToeCheckId, YawCheckId } from "@/types/identifiers";
import type { components } from "../generated/schema";

type PhotoResponse = components["schemas"]["PhotoResponse"];

export function useUploadCalibrationPhoto(
  buildId: BuildId,
  calId: CalibrationId,
  onProgress?: (progress: number) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, category_id }: { file: File; category_id: PhotoCategoryId }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category_id", category_id);
      formData.append("calibration_id", calId);

      const result = await uploadWithProgress<PhotoResponse>({
        url: `/api/builds/${buildId}/photos`,
        formData,
        onProgress,
      });
      return result.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useDeleteCalibrationPhoto(
  buildId: BuildId,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: PhotoId) => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/photos/{photo_id}",
        {
          params: { path: { build_id: buildId, photo_id: photoId } },
        },
      );
      return throwIfError(result, "Failed to delete photo");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useUploadToeCheckPhoto(
  buildId: BuildId,
  checkId: ToeCheckId,
  onProgress?: (progress: number) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, category_id }: { file: File; category_id: PhotoCategoryId }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category_id", category_id);
      formData.append("toe_check_id", checkId);

      const result = await uploadWithProgress<PhotoResponse>({
        url: `/api/builds/${buildId}/photos`,
        formData,
        onProgress,
      });
      return result.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useDeleteToeCheckPhoto(
  buildId: BuildId,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: PhotoId) => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/photos/{photo_id}",
        {
          params: { path: { build_id: buildId, photo_id: photoId } },
        },
      );
      return throwIfError(result, "Failed to delete photo");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useUploadYawCheckPhoto(
  buildId: BuildId,
  checkId: YawCheckId,
  onProgress?: (progress: number) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, category_id }: { file: File; category_id: PhotoCategoryId }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category_id", category_id);
      formData.append("yaw_check_id", checkId);

      const result = await uploadWithProgress<PhotoResponse>({
        url: `/api/builds/${buildId}/photos`,
        formData,
        onProgress,
      });
      return result.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useDeleteYawCheckPhoto(
  buildId: BuildId,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: PhotoId) => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/photos/{photo_id}",
        {
          params: { path: { build_id: buildId, photo_id: photoId } },
        },
      );
      return throwIfError(result, "Failed to delete photo");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}
